import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { useLatest } from 'lib/hooks/use-latest';
import { entityRefLabel } from 'utils/entity-id';
import { fetchScoredCandidates } from './api';
import type { ConsolidationAction } from './consolidation-utils';
import { scoredCandidate } from './reconcile-candidate-helpers';
import {
  classifyStaleSelection,
  type Fingerprints,
  refreshRows,
  reloadActions,
  type Rows,
  staleSelectionMessage,
} from './utils';

interface UseStaleSelectionOptions {
  open: boolean;
  // The anchor whose scored candidates the tearsheet was opened with.
  primaryEntityId: number | undefined;
  selectedIds: Set<number>;
  loadedFingerprints: Fingerprints | undefined;
  namesById: Record<number, string>;
  presentation: Rows;
  dispatch: Dispatch<ConsolidationAction>;
  refreshPreview: () => void;
  setError: (message: string) => void;
  setPresentation: Dispatch<SetStateAction<Rows>>;
}

/**
 * Recovers from a stale-selection 409: retired rows leave the model, the
 * selection is re-previewed for fresh fingerprints, and the candidate rows are
 * reloaded so changed rows show their current data.
 * @param {UseStaleSelectionOptions} options - The tearsheet's selection, rows and setters
 * @return {Function} The handler to call with the 409's current fingerprints
 */
export function useStaleSelection({
  open,
  primaryEntityId,
  selectedIds,
  loadedFingerprints,
  namesById,
  presentation,
  dispatch,
  refreshPreview,
  setError,
  setPresentation,
}: UseStaleSelectionOptions) {
  // Only the latest reload applies: one that lands after a newer one started,
  // or after the tearsheet closed, reopened, unmounted or changed anchor, is
  // dropped.
  const reloadSeq = useRef(0);
  useEffect(
    () => () => {
      reloadSeq.current += 1;
    },
    [open, primaryEntityId]
  );
  const rowsRef = useLatest(presentation);

  return (current: Fingerprints) => {
    const { retired, changed } = classifyStaleSelection(
      Array.from(selectedIds),
      current,
      loadedFingerprints
    );
    // The anchor is the tearsheet's subject, so it keeps its row even when the
    // server reports it gone; the message still names it.
    const removable = retired.filter((id) => id !== primaryEntityId);
    if (removable.length > 0)
      dispatch({ type: 'removeCandidates', ids: removable });
    refreshPreview();
    /**
     * Names a row for the message by its entity's name and id.
     * @param {number} id - The row's entity id
     * @return {string} The label
     */
    const label = (id: number) => entityRefLabel(namesById[id] ?? null, id);
    setError(staleSelectionMessage(changed.map(label), retired.map(label)));

    if (primaryEntityId === undefined) return;
    reloadSeq.current += 1;
    const seq = reloadSeq.current;
    fetchScoredCandidates(primaryEntityId)
      .then((response) => {
        if (seq !== reloadSeq.current) return;
        const fresh = (response.candidates ?? []).map(scoredCandidate);
        reloadActions(rowsRef.current, fresh).forEach((action) =>
          dispatch(action)
        );
        setPresentation((rows) => refreshRows(rows, fresh));
      })
      .catch(() => {
        // The rows stay as they were; the re-preview and message still apply.
      });
  };
}
